// Money Tree — aesthetic gradient edition. The canopy uses a multi-stop
// vertical gradient (violet → fuchsia → cyan) and the trunk + falling
// particles share the same palette. A soft outer glow gives the
// "aesthetic" lift the brand calls for.
//
// Motion: the $ coins breathe with a slow, desynchronized float and the
// falling particles drift downward in a continuous sub-loop. Both are purely
// decorative brand ambiance on the auth screen — kept subtle so they never
// distract from completing the form. prefers-reduced-motion → fully static.

import { motion, useReducedMotion } from "framer-motion";

const COINS = [
  { cx: 130, cy: 170, r: 22 },
  { cx: 210, cy: 140, r: 26 },
  { cx: 285, cy: 185, r: 22 },
  { cx: 165, cy: 235, r: 18 },
  { cx: 250, cy: 235, r: 20 },
  { cx: 205, cy: 285, r: 16 },
];

// Per-coin float timing — deliberately uneven so the six coins never bob in
// robotic unison. Amplitudes stay tiny (4–6px in a 520-unit viewBox).
const COIN_MOTION = [
  { dy: 5, dur: 3.6, delay: 0 },
  { dy: 6, dur: 4.4, delay: 0.5 },
  { dy: 4, dur: 3.9, delay: 0.9 },
  { dy: 5, dur: 4.7, delay: 0.3 },
  { dy: 4, dur: 4.1, delay: 1.2 },
  { dy: 6, dur: 3.4, delay: 0.7 },
];

const PARTICLES = [
  { x: 70, y: 360, r: 1.8, c: "#a78bfa" },
  { x: 55, y: 395, r: 1.4, c: "#f0abfc" },
  { x: 95, y: 410, r: 2.0, c: "#22d3ee" },
  { x: 110, y: 450, r: 1.5, c: "#fde68a" },
  { x: 80, y: 480, r: 1.7, c: "#c4b5fd" },
  { x: 330, y: 355, r: 1.6, c: "#a78bfa" },
  { x: 345, y: 405, r: 2.1, c: "#f0abfc" },
  { x: 310, y: 390, r: 1.4, c: "#22d3ee" },
  { x: 295, y: 450, r: 1.6, c: "#fde68a" },
  { x: 320, y: 485, r: 1.5, c: "#c4b5fd" },
  { x: 165, y: 430, r: 1.2, c: "#a78bfa" },
  { x: 235, y: 430, r: 1.2, c: "#22d3ee" },
];

export default function MoneyTree({ className = "" }) {
  const reduce = useReducedMotion();
  return (
    <svg
      viewBox="0 0 400 520"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        {/* Outer halo around the canopy */}
        <radialGradient id="mt-halo" cx="50%" cy="38%" r="55%">
          <stop offset="0%" stopColor="#c4b5fd" stopOpacity="0.55" />
          <stop offset="40%" stopColor="#a78bfa" stopOpacity="0.20" />
          <stop offset="100%" stopColor="#a78bfa" stopOpacity="0" />
        </radialGradient>

        {/* Vertical gradient that paints every dot via mask */}
        <linearGradient id="mt-canopy-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fef3c7" />       {/* warm gold top */}
          <stop offset="22%" stopColor="#f0abfc" />      {/* pink */}
          <stop offset="50%" stopColor="#c084fc" />      {/* violet */}
          <stop offset="78%" stopColor="#818cf8" />      {/* indigo */}
          <stop offset="100%" stopColor="#22d3ee" />     {/* cyan bottom */}
        </linearGradient>

        {/* Coin gradient */}
        <linearGradient id="mt-coin-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#fde68a" />
          <stop offset="50%" stopColor="#f0abfc" />
          <stop offset="100%" stopColor="#a78bfa" />
        </linearGradient>

        {/* Trunk gradient */}
        <linearGradient id="mt-trunk-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.6" />
        </linearGradient>

        {/* Soft glow filter */}
        <filter id="mt-glow" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="3.5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Triangle accent gradient */}
        <linearGradient id="mt-tri" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#c4b5fd" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.3" />
        </linearGradient>
      </defs>

      {/* Glow halo behind the canopy */}
      <circle cx="200" cy="190" r="180" fill="url(#mt-halo)" />

      {/* Triangular network accents */}
      <g stroke="url(#mt-tri)" strokeWidth="0.8" fill="none" opacity="0.55">
        <polygon points="55,175 130,135 90,235" />
        <polygon points="315,165 370,215 325,265" />
        <polygon points="200,50 255,90 165,90" />
        <polygon points="115,295 180,260 145,330" />
        <polygon points="245,300 295,275 270,340" />
        <polygon points="40,260 90,240 75,310" />
        <polygon points="335,280 380,300 355,355" />
      </g>

      {/* Trunk — curved + gradient */}
      <g filter="url(#mt-glow)">
        <path
          d="M195 500 Q 188 460 200 425 Q 215 385 195 350 Q 178 318 200 295"
          stroke="url(#mt-trunk-grad)"
          strokeWidth="4"
          fill="none"
          strokeLinecap="round"
        />
        {/* Branch lines */}
        <path d="M200 360 Q 175 350 158 340" stroke="url(#mt-trunk-grad)" strokeWidth="1.5" fill="none" opacity="0.6" strokeLinecap="round" />
        <path d="M200 350 Q 220 340 240 332" stroke="url(#mt-trunk-grad)" strokeWidth="1.5" fill="none" opacity="0.6" strokeLinecap="round" />
        <path d="M200 320 Q 180 310 165 305" stroke="url(#mt-trunk-grad)" strokeWidth="1.3" fill="none" opacity="0.5" strokeLinecap="round" />
        <path d="M200 310 Q 222 304 240 298" stroke="url(#mt-trunk-grad)" strokeWidth="1.3" fill="none" opacity="0.5" strokeLinecap="round" />
      </g>

      {/* Canopy outline (very soft) */}
      <path
        d="M200 65
           Q 100 70 70 165
           Q 30 235 90 290
           Q 110 325 170 325
           Q 200 340 230 330
           Q 290 335 320 295
           Q 370 250 335 175
           Q 320 80 230 65
           Q 215 60 200 65 Z"
        fill="url(#mt-halo)"
        opacity="0.6"
      />

      {/* Dotted canopy with vertical gradient */}
      <g filter="url(#mt-glow)">
        {generateLeafDots().map((d, i) => (
          <circle
            key={i}
            cx={d.x}
            cy={d.y}
            r={d.r}
            fill="url(#mt-canopy-grad)"
            opacity={d.o}
          />
        ))}
      </g>

      {/* Coin / dollar marks — gradient strokes, each floating on its own clock */}
      <g stroke="url(#mt-coin-grad)" strokeWidth="1.5" fill="none" filter="url(#mt-glow)">
        {COINS.map((coin, i) => (
          <Coin key={i} {...coin} motion={COIN_MOTION[i]} reduce={reduce} />
        ))}
      </g>

      {/* Falling money particles — multi color, drifting down in a soft loop */}
      <g filter="url(#mt-glow)">
        {PARTICLES.map((p, i) => (
          <motion.circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={p.r}
            fill={p.c}
            initial={false}
            animate={reduce ? { y: 0, opacity: 0.85 } : { y: [0, 7, 0], opacity: [0.85, 0.4, 0.85] }}
            transition={reduce ? undefined : {
              duration: 4 + (i % 4) * 0.8,
              delay: (i % 5) * 0.4,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </g>

      {/* Ground line — subtle */}
      <line x1="100" y1="510" x2="300" y2="510" stroke="url(#mt-tri)" strokeWidth="0.8" opacity="0.5" />
    </svg>
  );
}

function Coin({ cx, cy, r, motion: m, reduce }) {
  return (
    <motion.g
      initial={false}
      animate={reduce ? { y: 0 } : { y: [0, -m.dy, 0] }}
      transition={reduce ? undefined : {
        duration: m.dur,
        delay: m.delay,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    >
      <circle cx={cx} cy={cy} r={r} />
      <text
        x={cx}
        y={cy + r * 0.32}
        textAnchor="middle"
        fontSize={r * 1.0}
        fontFamily="system-ui, -apple-system, sans-serif"
        fontWeight="800"
        fill="url(#mt-coin-grad)"
        stroke="none"
      >
        $
      </text>
    </motion.g>
  );
}

// Deterministic seeded dot field for the canopy mass.
function generateLeafDots() {
  const out = [];
  let seed = 7;
  const rand = () => {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
  };
  const cx = 200, cy = 195, rx = 145, ry = 130;
  for (let i = 0; i < 360; i++) {
    const angle = rand() * Math.PI * 2;
    const t = Math.pow(rand(), 0.55);
    const x = cx + Math.cos(angle) * rx * t;
    const y = cy + Math.sin(angle) * ry * t;
    out.push({
      x: +x.toFixed(1),
      y: +y.toFixed(1),
      r: 0.7 + rand() * 1.8,
      o: 0.45 + rand() * 0.5,
    });
  }
  return out;
}
